import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

const CARD_THEMES = {
  blue: {
    gradient: 'from-blue-500 to-blue-600',
    iconBg: 'bg-blue-400/20',
    iconColor: 'text-white',
    shadow: 'shadow-blue-500/25',
    hoverShadow: 'hover:shadow-blue-500/40'
  },
  amber: {
    gradient: 'from-amber-500 to-orange-500',
    iconBg: 'bg-amber-400/20',
    iconColor: 'text-white',
    shadow: 'shadow-amber-500/25',
    hoverShadow: 'hover:shadow-amber-500/40'
  },
  emerald: {
    gradient: 'from-emerald-500 to-teal-500',
    iconBg: 'bg-emerald-400/20',
    iconColor: 'text-white',
    shadow: 'shadow-emerald-500/25',
    hoverShadow: 'hover:shadow-emerald-500/40'
  },
  violet: {
    gradient: 'from-violet-500 to-purple-600',
    iconBg: 'bg-violet-400/20',
    iconColor: 'text-white',
    shadow: 'shadow-violet-500/25',
    hoverShadow: 'hover:shadow-violet-500/40'
  },
  rose: {
    gradient: 'from-rose-500 to-pink-500',
    iconBg: 'bg-rose-400/20',
    iconColor: 'text-white',
    shadow: 'shadow-rose-500/25',
    hoverShadow: 'hover:shadow-rose-500/40'
  },
  slate: {
    gradient: 'from-slate-600 to-slate-700',
    iconBg: 'bg-slate-400/20',
    iconColor: 'text-white',
    shadow: 'shadow-slate-500/25',
    hoverShadow: 'hover:shadow-slate-500/40'
  }
};

const StatsCard = ({ 
  title, 
  value, 
  icon: Icon, 
  theme = 'blue',
  onClick,
  trend,
  trendValue,
  subtitle
}) => {
  const themeConfig = CARD_THEMES[theme] || CARD_THEMES.blue;
  
  return (
    <div 
      className={`
        relative overflow-hidden rounded-2xl p-5 sm:p-6
        bg-gradient-to-br ${themeConfig.gradient}
        shadow-lg ${themeConfig.shadow}
        ${onClick ? `cursor-pointer ${themeConfig.hoverShadow} hover:shadow-xl hover:-translate-y-0.5` : ''}
        transition-all duration-300 ease-out
        group
      `}
      onClick={onClick}
    >
      {/* Background decoration */}
      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/5 blur-2xl" />
      <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-black/5" />
      
      {/* Content */}
      <div className="relative z-10 flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white/80 truncate">
            {title}
          </p>
          <p className="mt-2 text-3xl sm:text-4xl font-bold text-white tracking-tight">
            {(value ?? 0).toLocaleString()}
          </p>
          
          {/* Trend indicator or subtitle */}
          {trend && trendValue && (
            <div className={`mt-2 flex items-center gap-1 text-sm ${
              trend === 'up' ? 'text-emerald-200' : 'text-rose-200'
            }`}>
              {trend === 'up' ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              <span className="font-medium">{trendValue}</span>
            </div>
          )}
          
          {subtitle && !trend && (
            <p className="mt-2 text-sm text-white/60 truncate">
              {subtitle}
            </p>
          )}
        </div>
        
        {/* Icon container */}
        <div className={`
          flex-shrink-0 p-3 rounded-xl ${themeConfig.iconBg}
          backdrop-blur-sm
          group-hover:scale-110 transition-transform duration-300
        `}>
          <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${themeConfig.iconColor}`} strokeWidth={1.5} />
        </div>
      </div>
      
      {/* Hover arrow indicator */}
      {onClick && (
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <svg className="w-5 h-5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default StatsCard;